import React, { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import moment from 'moment/moment';
import * as classnames from 'classnames';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { Tooltip } from 'components/atoms/tooltip';
import { ITepEntryPass, ITepLease } from 'types/tep';
import { SvgWrapper } from 'components/atoms/svg-wrapper';
import { ConfirmDeleteModal } from './confirm-delete-modal';
import { LeasesStatuses, TepIntents } from 'constants/tep';
import { leasesCellSelector } from 'core/redux/selectors/tep';
import { actions as leasesActions } from 'core/redux/api/veta/tep/leases';
import { close, draft, edit2, leaseIssue, returnIcon, save, undraft } from 'assets/icons';

import styles from './styles.module.css';

interface IActionsProps {
  isSelected: boolean;
  entryPass: ITepEntryPass;
}

export const ActionsCell: FC<IActionsProps> = ({ isSelected, entryPass }) => {
  const dispatch = useDispatch();

  const { id, intent, isRowsSelected } = useSelector(leasesCellSelector);

  const { reset, handleSubmit } = useFormContext<Partial<ITepLease>>();

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const [position, setPosition] = useState({ top: 0, left: 0 });

  const isDisabled = !isSelected && isRowsSelected;

  const wrapperClassname = classnames(styles.wrapper, {
    [styles.rowSelected]: isSelected,
    [styles.rowDisabled]: isDisabled,
  });

  const onCellClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const updateConfirmPosition = () => {
    if (wrapperRef.current) {
      const { top, left } = wrapperRef.current.getBoundingClientRect();

      setPosition({ top: top + 35, left: left - 20 });
    }
  };

  const openDraftModal = () => {
    dispatch(leasesActions.select({ leases: [entryPass] }));
    dispatch(leasesActions.intent({ intent: TepIntents.Draft }));
  };

  const openIssueModal = () => {
    dispatch(leasesActions.select({ leases: [entryPass] }));
    dispatch(leasesActions.intent({ intent: TepIntents.Issue }));
  };

  const openReturnModal = () => {
    dispatch(leasesActions.select({ leases: [entryPass] }));
    dispatch(leasesActions.intent({ intent: TepIntents.Return }));
  };

  const inlineEdit = () => {
    const { email, plateNumber, startOn, endOn } = entryPass.lease;

    dispatch(leasesActions.edit({ id: entryPass.id }));
    dispatch(leasesActions.intent({ intent: TepIntents.InlineEdit }));
    reset({ email, plateNumber, startOn, endOn });
  };

  const saveEntryPass = () => {
    if (id) {
      handleSubmit((form) => {
        const { email, plateNumber, startOn, endOn } = form;

        const data = {
          email,
          plateNumber,
          startOn: moment(startOn).format('YYYY-MM-DD'),
          endOn: moment(endOn).format('YYYY-MM-DD'),
        };

        dispatch(leasesActions.update.request({ id, data }));
      })();
    }
  };

  const resetIntent = () => {
    dispatch(leasesActions.edit({ id: null }));
  };

  const inlineUndraft = () => {
    dispatch(leasesActions.edit({ id: entryPass.id }));
    dispatch(leasesActions.intent({ intent: TepIntents.InlineUndraft }));
  };

  const undraftEntryPass = () => {
    dispatch(leasesActions.undraft.request({ id }));
  };

  const renderButtons = () => {
    if (entryPass.status === LeasesStatuses.Available) {
      return (
        <Tooltip text="draft">
          <SvgWrapper width={28} height={28} details={draft} onClick={openDraftModal} />
        </Tooltip>
      );
    }

    if (entryPass.status === LeasesStatuses.Drafted) {
      if (intent === TepIntents.InlineEdit && entryPass.id === id) {
        return (
          <>
            <Tooltip text="save">
              <SvgWrapper width={28} height={28} details={save} onClick={saveEntryPass} />
            </Tooltip>
            <Tooltip text="close without saving">
              <SvgWrapper width={28} height={28} details={close} onClick={resetIntent} />
            </Tooltip>
          </>
        );
      }

      return (
        <>
          <Tooltip text="edit">
            <SvgWrapper width={28} height={28} details={edit2} onClick={inlineEdit} />
          </Tooltip>
          <Tooltip text="issue">
            <SvgWrapper width={28} height={28} details={leaseIssue} onClick={openIssueModal} />
          </Tooltip>
          <Tooltip text="undraft">
            <SvgWrapper width={28} height={28} details={undraft} onClick={inlineUndraft} />
          </Tooltip>
          {intent === TepIntents.InlineUndraft && entryPass.id === id && (
            <ConfirmDeleteModal position={position} onCancel={resetIntent} undraftEntryPass={undraftEntryPass} />
          )}
        </>
      );
    }

    if (entryPass.status === LeasesStatuses.Issued) {
      return (
        <Tooltip text="return">
          <SvgWrapper width={28} height={28} details={returnIcon} onClick={openReturnModal} />
        </Tooltip>
      );
    }

    return null;
  };

  useEffect(() => {
    updateConfirmPosition();
  }, []);

  return (
    <div ref={wrapperRef} className={wrapperClassname} onClick={onCellClick}>
      {renderButtons()}
    </div>
  );
};
